import React, { useState, Fragment } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { useEstimates } from '../hooks/use-estimates';
import { useToast } from '../hooks/use-toast';
import { getQueryFn } from '../lib/queryClient';
import { authInterceptor } from '../lib/auth-interceptor';
import { formatCurrency } from '../lib/utils';
import StatusBadge from '../components/shared/status-badge';
import { 
  ChevronDown, 
  FileText, 
  Plus, 
  FileEdit, 
  Mail, 
  Download, 
  Eye, 
  Trash2,
  BanknoteIcon,
  Calculator,
  XCircle,
  Check,
  Printer,
  CheckCircle,
  Clock,
  Search,
  Settings,
  Edit,
  User,
  Calendar,
  X,
  Send,
  ChevronUp,
  ChevronsUpDown,
  Filter,
  MoreVertical
} from "lucide-react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { format } from "date-fns";
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import PageHeader from '../components/shared/page-header';
import EstimateDetail from '../components/estimates/estimate-detail';
import EstimateForm from '../components/estimates/estimate-form';
import EstimateLineItems from '../components/estimates/estimate-line-items';
import { 
  Dialog, 
  DialogContent,
  DialogTitle,
  DialogDescription
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import TopNav from '../components/layout/top-nav';
import { Input } from '../components/ui/input';

// Helper function to get status badge style (standardized)
const getStatusBadge = (status: string) => {
  return <StatusBadge status={status} type="estimate" />;
};

export default function EstimatesPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("date_desc");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [expandedEstimates, setExpandedEstimates] = useState<Set<number>>(new Set());
  const [selectedEstimate, setSelectedEstimate] = useState<any>(null);
  const [estimateType, setEstimateType] = useState<string>("agent");
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    action: () => void;
    confirmText: string;
    cancelText: string;
  }>({
    isOpen: false,
    title: '',
    description: '',
    action: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });
  
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Use the estimates hook for all estimate-related operations
  const { 
    estimates,
    isLoadingEstimates: isLoading,
    estimatesError: error,
    refetchEstimates: refetch,
    deleteEstimateMutation, 
    convertToInvoiceMutation,
    updateEstimateStatusMutation,
    resendEstimateMutation
  } = useEstimates();

  // Debug: Log estimates data
  console.log('🔍 DEBUG: Estimates page - estimates data:', estimates);
  console.log('🔍 DEBUG: Estimates page - estimates length:', Array.isArray(estimates) ? estimates.length : 'not an array');
  console.log('🔍 DEBUG: Estimates page - isLoading:', isLoading);
  console.log('🔍 DEBUG: Estimates page - error:', error);
  
  // Filter and sort estimates
  const filteredEstimates = (estimates || []) && Array.isArray(estimates)
    ? estimates
        .filter((estimate: any) => {
          // Status filter
          if (statusFilter !== "all" && estimate.status !== statusFilter) {
            return false;
          }
          
          // Search filter
          if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            return (
              (estimate.estimate_number || estimate.estimateNumber || '').toLowerCase().includes(lowerCaseQuery) ||
              estimate.client?.first_name?.toLowerCase().includes(lowerCaseQuery) ||
              estimate.client?.last_name?.toLowerCase().includes(lowerCaseQuery) ||
              (estimate.project?.title && estimate.project.title.toLowerCase().includes(lowerCaseQuery))
            );
          }
          
          return true;
        })
        .sort((a: any, b: any) => {
          let comparison = 0;
          
          switch (sortBy) {
            case "estimate":
              const aEstimate = (a.estimate_number || a.estimateNumber || '').toLowerCase();
              const bEstimate = (b.estimate_number || b.estimateNumber || '').toLowerCase();
              comparison = aEstimate.localeCompare(bEstimate);
              break;
            case "client":
              const aClient = `${a.client?.first_name || ''} ${a.client?.last_name || ''}`.toLowerCase();
              const bClient = `${b.client?.first_name || ''} ${b.client?.last_name || ''}`.toLowerCase();
              comparison = aClient.localeCompare(bClient);
              break;
            case "amount":
              comparison = parseFloat(a.total || 0) - parseFloat(b.total || 0);
              break;
            case "status":
              comparison = (a.status || '').localeCompare(b.status || '');
              break;
            case "date":
              comparison = new Date(a.created_at || a.createdAt).getTime() - new Date(b.created_at || b.createdAt).getTime();
              break;
            default:
              comparison = new Date(b.created_at || b.createdAt).getTime() - new Date(a.created_at || a.createdAt).getTime();
          }
          
          return sortDirection === "asc" ? comparison : -comparison;
        })
    : [];

  const totalPages = Math.max(1, Math.ceil(filteredEstimates.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedEstimates = filteredEstimates.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const viewEstimateDetails = async (estimate: any) => {
    try {
      // Get user data for contractor ID
      const userData = localStorage.getItem('user');
      let contractorId = '';
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          contractorId = user.id?.toString() || '';
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      // Get token for this estimate and open public view
      const response = await fetch(`/api/protected/estimates/get-token?id=${estimate.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          ...(contractorId != null && { 'X-Contractor-ID': String(contractorId) }),
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Open estimate with token in new tab
        window.open(data.publicUrl, '_blank');
      } else {
        // If token fetch fails, fallback to internal detail page
        console.warn('Could not get token, opening internal detail page');
        setLocation(`/estimates/edit/${estimate.id}`);
      }
    } catch (error) {
      console.error('Error getting estimate token:', error);
      // Fallback to internal detail page
      setLocation(`/estimates/edit/${estimate.id}`);
    }
  };

  const createNewEstimate = () => {
    setLocation('/estimates/create');
  };

  const handleCreateEstimate = () => {
    switch (estimateType) {
      case "standard":
        setLocation('/estimates/create');
        break;
      case "multi-service":
        setLocation('/estimates/multi-service');
        break;
      case "professional":
        setLocation('/estimates/professional');
        break;
      case "premium":
        // Premium estimates are for viewing existing ones, redirect to standard creation
        setLocation('/estimates/create');
        break;
      case "agent":
        // Legacy agent estimate form is deprecated; use standard estimate create page.
        setLocation('/estimates/create');
        break;
      case "agent-service":
        setLocation('/agents/service-estimate');
        break;
      default:
        setLocation('/estimates/create');
    }
  };

  const editEstimate = (estimate: any) => {
    if (estimate.status === 'accepted' || estimate.status === 'converted') {
      toast({
        title: 'Cannot edit',
        description: 'Signed estimates are locked. Prices and descriptions cannot be changed.',
        variant: 'destructive',
      });
      return;
    }
    setLocation(`/estimates/edit/${estimate.id}`);
  };

  const printEstimate = (estimate: any) => {
    // Open estimate in new window for printing
    window.open(`/estimates/${estimate.id}/print`, '_blank');
  };

  const deleteEstimate = async (estimate: any) => {
    if (window.confirm(`Are you sure you want to delete estimate ${estimate.estimate_number || estimate.estimateNumber || 'N/A'}?`)) {
      try {
        await deleteEstimateMutation.mutateAsync(estimate.id);
        toast({
          title: "Estimate Deleted",
          description: "The estimate has been successfully deleted.",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete estimate. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleStatusChange = (estimate: any, status: string) => {
    const statusMessages = {
      'sent': {
        title: 'Mark as Sent',
        description: `Are you sure you want to mark estimate ${estimate.estimate_number || estimate.estimateNumber || 'N/A'} as sent to the client?`,
        confirmText: 'Send',
        cancelText: 'Cancel'
      },
      'accepted': {
        title: 'Accept Estimate',
        description: `Are you sure you want to accept estimate ${estimate.estimate_number || estimate.estimateNumber || 'N/A'}? This will mark it as approved by the client.`,
        confirmText: 'Accept',
        cancelText: 'Cancel'
      },
      'rejected': {
        title: 'Reject Estimate',
        description: `Are you sure you want to reject estimate ${estimate.estimate_number || estimate.estimateNumber || 'N/A'}? This will mark it as declined by the client.`,
        confirmText: 'Reject',
        cancelText: 'Cancel'
      }
    };

    const message = statusMessages[status as keyof typeof statusMessages];
    
    setConfirmDialog({
      isOpen: true,
      title: message.title,
      description: message.description,
      action: () => {
        updateEstimateStatusMutation.mutate(
          { id: estimate.id, status },
          {
            onSuccess: () => {
              toast({
                title: "Status Updated",
                description: `Estimate status has been updated to ${status}.`,
              });
              setConfirmDialog(prev => ({ ...prev, isOpen: false }));
            }
          }
        );
      },
      confirmText: message.confirmText,
      cancelText: message.cancelText
    });
  };

  const handleResendEstimate = (estimate: any) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Re-send Estimate',
      description: `Are you sure you want to re-send estimate ${estimate.estimate_number || estimate.estimateNumber || 'N/A'} to the client?`,
      action: () => {
        resendEstimateMutation.mutate(estimate.id);
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
      confirmText: 'Re-send',
      cancelText: 'Cancel'
    });
  };

  const handleConvertToInvoice = (estimate: any) => {
    if (estimate.status !== 'accepted') {
      toast({
        title: "Action not allowed",
        description: "Only accepted estimates can be converted to work orders.",
        variant: "destructive",
      });
      return;
    }
    
    setConfirmDialog({
      isOpen: true,
      title: 'Convert to Work Order',
              description: `Are you sure you want to convert estimate ${estimate.estimate_number || estimate.estimateNumber || 'N/A'} to a work order? This will create an invoice and mark the estimate as converted.`,
      action: () => {
        convertToInvoiceMutation.mutate(estimate.id, {
          onSuccess: (data) => {
            toast({
              title: "Work Order Created",
              description: `Work order has been created from the estimate.`,
            });
            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          }
        });
      },
      confirmText: 'Convert',
      cancelText: 'Cancel'
    });
  };

  const [sendingEstimate, setSendingEstimate] = useState<number | null>(null);

  const handleSendEstimateClick = (estimate: any) => {
    const clientName = estimate.client?.firstName && estimate.client?.lastName
      ? `${estimate.client.firstName} ${estimate.client.lastName}`
      : estimate.client?.first_name && estimate.client?.last_name
      ? `${estimate.client.first_name} ${estimate.client.last_name}`
      : estimate.client?.email || 'the client';
    const email = estimate.client?.email || 'their email';
    setConfirmDialog({
      isOpen: true,
      title: 'Send estimate',
      description: `Send estimate ${estimate.estimate_number || estimate.estimateNumber || 'N/A'} to ${clientName} (${email})? They will receive an email with a link to view and approve.`,
      action: () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        handleSendEstimate(estimate);
      },
      confirmText: 'Send',
      cancelText: 'Cancel'
    });
  };

  const handleSendEstimate = async (estimate: any) => {
    try {
      setSendingEstimate(estimate.id);
      console.log('📧 Sending estimate via email:', estimate.id);
      
      const response = await fetch(`/api/protected/estimates/send-email?id=${estimate.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authInterceptor.addAuthHeaders(),
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Estimate Sent! 📧",
          description: `Estimate has been sent to ${result.clientEmail || 'your client'}. They can now review and approve it.`,
        });
        
        // Refresh the estimates list
        refetch();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to send estimate');
      }
    } catch (error) {
      console.error('❌ Error sending estimate:', error);
      toast({
        title: "Error Sending Estimate",
        description: error instanceof Error ? error.message : "Could not send estimate. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendingEstimate(null);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("desc");
    }
  };

  const toggleExpandedEstimate = (estimateId: number) => {
    const newExpanded = new Set(expandedEstimates);
    if (newExpanded.has(estimateId)) {
      newExpanded.delete(estimateId);
    } else {
      newExpanded.add(estimateId);
    }
    setExpandedEstimates(newExpanded);
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) {
      return <ChevronsUpDown className="h-4 w-4 text-slate-400" />;
    }
    return sortDirection === "asc" 
      ? <ChevronUp className="h-4 w-4 text-[var(--dash-accent)]" />
      : <ChevronDown className="h-4 w-4 text-[var(--dash-accent)]" />;
  };

  const calculateTotal = () => {
    if (!estimates || !Array.isArray(estimates)) return { total: 0, count: 0 };
    
    const total = estimates.reduce((sum: number, estimate: any) => {
      return sum + parseFloat(estimate.total || 0);
    }, 0);
    
    return { total, count: estimates.length };
  };

  const { total, count } = calculateTotal();

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-4 md:p-6 pt-24 flex flex-col items-center">
            <div className="w-full space-y-3">
              <div>
                <h1 className="text-2xl font-bold text-[var(--dash-text)]">Estimates</h1>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Estimates created by your agents for clients. View and manage them here.</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--dash-text-muted)]" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)] text-[var(--dash-text)] placeholder:text-[var(--dash-text-muted)]"
                  />
                </div>
                <Select value={statusFilter} onValueChange={(v: string) => setStatusFilter(v)}>
                  <SelectTrigger className="w-[140px] h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)] text-[var(--dash-text)]">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
                {/* LOCKED: Must go to /estimates/create (Agent form). See docs/ESTIMATE_FORM_LOCK.md */}
                <Button onClick={() => setLocation('/estimates/create')} className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white ml-auto">
                  <FileText className="h-4 w-4 mr-2" />
                  Create estimate
                </Button>
                <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)]" onClick={() => {
                  let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
                  csvContent += "Estimate Number,Client Name,Client Email,Service Type,Status,Total,Issue Date,Expiry Date,Description\n";
                  (estimates as any[] || []).forEach((est: any) => {
                    const estimateNumber = est.estimate_number || est.estimateNumber || '';
                    const clientName = `${est.client?.first_name || est.client?.firstName || ''} ${est.client?.last_name || est.client?.lastName || ''}`.trim() || 'Unknown';
                    const clientEmail = est.client?.email || 'N/A';
                    const serviceType = est.service_type || est.serviceType || 'General';
                    const status = est.status || 'draft';
                    const total = parseFloat(est.total || 0);
                    const issueDate = est.issue_date || est.issueDate ? new Date(est.issue_date || est.issueDate).toLocaleDateString() : 'N/A';
                    const expiryDate = est.expiry_date || est.expiryDate ? new Date(est.expiry_date || est.expiryDate).toLocaleDateString() : 'N/A';
                    const description = (est.description || est.ai_description || est.aiDescription || 'No description').replace(/"/g, '""').replace(/\n/g, ' ');
                    csvContent += `"${estimateNumber}","${clientName}","${clientEmail}","${serviceType}","${status}","$${total.toFixed(2)}","${issueDate}","${expiryDate}","${description}"\n`;
                  });
                  const link = document.createElement("a");
                  link.setAttribute("href", encodeURI(csvContent));
                  link.setAttribute("download", "estimates_with_details.csv");
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  toast({ title: "Export Complete", description: "Estimates exported to CSV" });
                }}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)]" onClick={() => window.location.href = '/settings'}>
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><FileText className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{(estimates as any[])?.length || 0}</span>
                    <span className="dash-stat-delta positive">+{(estimates as any[])?.filter((e: any) => new Date(e.created_at || e.createdAt).getMonth() === new Date().getMonth()).length || 0}</span>
                  </div>
                  <div className="dash-stat-label">Total estimates</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><FileText className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{(estimates as any[])?.filter((e: any) => e.status === 'draft').length || 0}</span>
                    <span className="dash-stat-delta positive">+0</span>
                  </div>
                  <div className="dash-stat-label">Draft</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><FileText className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{(estimates as any[])?.filter((e: any) => e.status === 'sent').length || 0}</span>
                    <span className="dash-stat-delta positive">+0</span>
                  </div>
                  <div className="dash-stat-label">Sent</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><FileText className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{formatCurrency((estimates as any[])?.reduce((sum: number, e: any) => sum + (e.total || 0), 0) || 0)}</span>
                  </div>
                  <div className="dash-stat-label">Total value</div>
                </div>
              </div>

          {/* Table card */}
          <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--dash-accent)] border-t-transparent mx-auto" />
                <p className="mt-2 text-sm text-[var(--dash-text-muted)]">Loading estimates...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-400">Error loading estimates: {(error as any)?.message || 'Unknown error'}</p>
              </div>
            ) : filteredEstimates.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-6">📋</div>
                <div className="text-xl font-bold text-[var(--dash-text)] mb-2">No Estimates Found</div>
                <div className="text-sm text-[var(--dash-text-muted)] mb-6">
                  {searchQuery ? `No estimates match "${searchQuery}"` : "Start by creating your first estimate"}
                </div>
                {/* LOCKED: Must go to /estimates/create (Agent form). See docs/ESTIMATE_FORM_LOCK.md */}
                <Button className="h-10 rounded-lg bg-[var(--dash-accent)] text-white" onClick={() => setLocation('/estimates/create')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Create First Estimate
                </Button>
              </div>
            ) : (
              <>
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="border-b border-[var(--dash-border)]">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider cursor-pointer hover:text-[var(--dash-text)]" onClick={() => handleSort("estimate")}>
                          <div className="flex items-center gap-1">Estimate {getSortIcon("estimate")}</div>
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider cursor-pointer hover:text-[var(--dash-text)]" onClick={() => handleSort("client")}>
                          <div className="flex items-center gap-1">Client {getSortIcon("client")}</div>
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider cursor-pointer hover:text-[var(--dash-text)]" onClick={() => handleSort("amount")}>
                          <div className="flex items-center gap-1">Amount {getSortIcon("amount")}</div>
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider cursor-pointer hover:text-[var(--dash-text)]" onClick={() => handleSort("status")}>
                          <div className="flex items-center gap-1">Status {getSortIcon("status")}</div>
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider cursor-pointer hover:text-[var(--dash-text)]" onClick={() => handleSort("date")}>
                          <div className="flex items-center gap-1">Date {getSortIcon("date")}</div>
                        </th>
                        <th className="text-center py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase w-20">Details</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                    {paginatedEstimates.map((estimate) => (
                      <Fragment key={estimate.id}>
                        <tr key={estimate.id} className="border-b border-[var(--dash-border)] hover:bg-slate-50/80">
                          <td className="py-2 px-3">
                            <Link href={`/estimates/edit/${estimate.id}`} className="flex flex-wrap items-center gap-2 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--dash-accent)] focus:ring-offset-1 rounded-lg">
                              <div className="w-8 h-8 rounded-lg bg-[var(--dash-accent)] flex items-center justify-center flex-shrink-0">
                                <FileText className="h-4 w-4 text-white" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-[var(--dash-text)]">
                                  {estimate.estimate_number || estimate.estimateNumber || 'N/A'}
                                </div>
                                {estimate.title && !String(estimate.title).startsWith('EST-') && (
                                  <div className="text-xs text-[var(--dash-text-muted)] truncate max-w-[150px]">{estimate.title}</div>
                                )}
                              </div>
                            </Link>
                          </td>
                          <td className="py-2 px-3">
                            <div className="text-sm text-[var(--dash-text)]">
                              {estimate.client?.firstName && estimate.client?.lastName 
                                ? `${estimate.client.firstName} ${estimate.client.lastName}`
                                : estimate.client?.first_name && estimate.client?.last_name 
                                ? `${estimate.client.first_name} ${estimate.client.last_name}`
                                : estimate.client?.name 
                                ? estimate.client.name
                                : estimate.client_name
                                ? estimate.client_name
                                : 'No Client'
                              }
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <div className="text-sm font-semibold text-[var(--dash-accent)]">
                              {formatCurrency(estimate.total || 0)}
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            {getStatusBadge(estimate.status)}
                          </td>
                          <td className="py-2 px-3">
                            <div className="text-sm text-[var(--dash-text-muted)]">
                              {new Date(estimate.created_at || estimate.createdAt).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex items-center justify-center">
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-[var(--dash-border)] h-9 w-9 p-0"
                                onClick={() => toggleExpandedEstimate(estimate.id)}
                                title={expandedEstimates.has(estimate.id) ? "Hide Details" : "Show Details"}
                              >
                                <ChevronDown className={`h-4 w-4 transition-transform ${expandedEstimates.has(estimate.id) ? 'rotate-180' : ''}`} />
                              </Button>
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex items-center justify-end">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[var(--dash-text-muted)] hover:text-[var(--dash-text)]" title="Actions">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-48 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)]" align="end">
                                  {/* Status-based actions */}
                                  {estimate.status === 'draft' && (
                                    <>
                                      <DropdownMenuItem 
                                        onClick={() => handleSendEstimateClick(estimate)}
                                        disabled={updateEstimateStatusMutation.isPending || sendingEstimate === estimate.id}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        {sendingEstimate === estimate.id ? (
                                          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                        ) : (
                                          <Send className="h-4 w-4 mr-2" />
                                        )}
                                        <span>{sendingEstimate === estimate.id ? 'Sending...' : 'Send Estimate'}</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => editEstimate(estimate)}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Edit className="h-4 w-4 mr-2" />
                                        <span>Edit</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  
                                  {estimate.status === 'sent' && (
                                    <>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'accepted')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-accent)] hover:bg-[var(--dash-accent)]/10 cursor-pointer"
                                      >
                                        <Check className="h-4 w-4 mr-2" />
                                        <span>Accept</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'rejected')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-pill-inactive)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <X className="h-4 w-4 mr-2" />
                                        <span>Reject</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleResendEstimate(estimate)}
                                        disabled={resendEstimateMutation.isPending}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Send className="h-4 w-4 mr-2" />
                                        <span>Re-send</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => editEstimate(estimate)}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Edit className="h-4 w-4 mr-2" />
                                        <span>Edit</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  
                                  {estimate.status === 'rejected' && (
                                    <>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'sent')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Send className="h-4 w-4 mr-2" />
                                        <span>Resend</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'accepted')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-accent)] hover:bg-[var(--dash-accent)]/10 cursor-pointer"
                                      >
                                        <Check className="h-4 w-4 mr-2" />
                                        <span>Accept</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => editEstimate(estimate)}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Edit className="h-4 w-4 mr-2" />
                                        <span>Edit</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  
                                  {estimate.status === 'accepted' && (
                                    <>
                                      <DropdownMenuItem 
                                        onClick={() => handleConvertToInvoice(estimate)}
                                        disabled={convertToInvoiceMutation.isPending}
                                        className="text-[var(--dash-accent)] hover:bg-[var(--dash-accent)]/10 cursor-pointer"
                                      >
                                        <BanknoteIcon className="h-4 w-4 mr-2" />
                                        <span>Convert to Invoice</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'rejected')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-pill-inactive)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <X className="h-4 w-4 mr-2" />
                                        <span>Client Said No</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  
                                  {estimate.status === 'converted' && (
                                    <>
                                      <DropdownMenuItem 
                                        disabled
                                        className="text-green-400 dark:text-green-500 cursor-not-allowed opacity-60"
                                      >
                                        <Check className="h-4 w-4 mr-2" />
                                        <span>Converted to Invoice</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  
                                  {/* Always available actions */}
                                  <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-600" />
                                  <DropdownMenuItem 
                                    onClick={() => viewEstimateDetails(estimate)}
                                    className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    <span>View Details</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => printEstimate(estimate)}
                                    className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                  >
                                    <Printer className="h-4 w-4 mr-2" />
                                    <span>Print</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-600" />
                                  <DropdownMenuItem 
                                    onClick={() => deleteEstimate(estimate)}
                                    className="text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 cursor-pointer"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    <span>Delete</span>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expandable row for line items */}
                        {expandedEstimates.has(estimate.id) && (
                          <tr>
                            <td colSpan={7} className="p-0">
                              <div className="bg-slate-50 border-t border-[var(--dash-border)]">
                                {estimate.status === 'rejected' && estimate.notes && (
                                  <div className="px-4 pt-3 pb-1">
                                    <p className="text-sm font-medium text-red-800 dark:text-red-200">Reason for rejection:</p>
                                    <p className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap">{estimate.notes}</p>
                                  </div>
                                )}
                                <EstimateLineItems 
                                  items={estimate.items || []}
                                  estimateNumber={estimate.estimate_number || estimate.estimateNumber || 'N/A'}
                                  className="m-4"
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-t border-[var(--dash-border)] bg-[var(--dash-bg)]">
                  <div className="flex items-center gap-2 text-sm text-[var(--dash-text-muted)]">
                    <Select value={String(pageSize)} onValueChange={(v: string) => { setPageSize(Number(v)); setPage(1); }}>
                      <SelectTrigger className="w-[120px] h-9 rounded-lg border-[var(--dash-border)]">
                        <SelectValue placeholder="50 records" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 records</SelectItem>
                        <SelectItem value="25">25 records</SelectItem>
                        <SelectItem value="50">50 records</SelectItem>
                        <SelectItem value="100">100 records</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="ml-2">
                      {(currentPage - 1) * pageSize + 1} – {Math.min(currentPage * pageSize, filteredEstimates.length)} of {filteredEstimates.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-9 w-9 p-0 rounded-lg" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}>←</Button>
                    {(() => {
                      const show = Math.min(5, totalPages);
                      let start = currentPage <= 2 ? 1 : Math.min(currentPage - 2, totalPages - show + 1);
                      start = Math.max(1, start);
                      const pages: number[] = [];
                      for (let i = 0; i < show; i++) pages.push(start + i);
                      return pages.map((p) => (
                        <Button key={p} variant={p === currentPage ? 'default' : 'outline'} size="sm" className="h-9 w-9 p-0 rounded-lg" onClick={() => setPage(p)}>{p}</Button>
                      ));
                    })()}
                    <Button variant="outline" size="sm" className="h-9 w-9 p-0 rounded-lg" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>→</Button>
                  </div>
                </div>

                {/* Mobile: Card Layout */}
                <div className="lg:hidden space-y-3 p-4">
                  {paginatedEstimates.map((estimate) => (
                    <Card key={estimate.id} className="bg-[var(--dash-card)] border-[var(--dash-border)]">
                      <CardContent className="p-3">
                        <div className="space-y-3">
                          {/* Header Row */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-9 h-9 bg-[var(--dash-accent)] rounded flex items-center justify-center flex-shrink-0">
                                <FileText className="h-4 w-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-[var(--dash-text)] truncate">
                                  {estimate.estimate_number || estimate.estimateNumber || 'N/A'}
                                </div>
                                {estimate.title && (
                                  <div className="text-xs text-[var(--dash-text-muted)] truncate">{estimate.title}</div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-[var(--dash-border)] h-9 w-9 p-0"
                                onClick={() => toggleExpandedEstimate(estimate.id)}
                                title={expandedEstimates.has(estimate.id) ? "Hide Details" : "Show Details"}
                              >
                                <ChevronDown className={`h-4 w-4 transition-transform ${expandedEstimates.has(estimate.id) ? 'rotate-180' : ''}`} />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-10 w-10 p-0 text-[var(--dash-text-muted)] hover:text-[var(--dash-text)] hover:bg-[var(--dash-card)]"
                                    title="Actions"
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)]" align="end">
                                  {estimate.status === 'draft' && (
                                    <>
                                      <DropdownMenuItem 
                                        onClick={() => handleSendEstimateClick(estimate)}
                                        disabled={updateEstimateStatusMutation.isPending || sendingEstimate === estimate.id}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        {sendingEstimate === estimate.id ? (
                                          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                        ) : (
                                          <Send className="h-4 w-4 mr-2" />
                                        )}
                                        <span>{sendingEstimate === estimate.id ? 'Sending...' : 'Send Estimate'}</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => editEstimate(estimate)}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Edit className="h-4 w-4 mr-2" />
                                        <span>Edit</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {estimate.status === 'sent' && (
                                    <>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'accepted')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-accent)] hover:bg-[var(--dash-accent)]/10 cursor-pointer"
                                      >
                                        <Check className="h-4 w-4 mr-2" />
                                        <span>Accept</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'rejected')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-pill-inactive)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <X className="h-4 w-4 mr-2" />
                                        <span>Reject</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleResendEstimate(estimate)}
                                        disabled={resendEstimateMutation.isPending}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Send className="h-4 w-4 mr-2" />
                                        <span>Re-send</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => editEstimate(estimate)}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Edit className="h-4 w-4 mr-2" />
                                        <span>Edit</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {estimate.status === 'rejected' && (
                                    <>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'sent')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Send className="h-4 w-4 mr-2" />
                                        <span>Resend</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'accepted')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-accent)] hover:bg-[var(--dash-accent)]/10 cursor-pointer"
                                      >
                                        <Check className="h-4 w-4 mr-2" />
                                        <span>Accept</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => editEstimate(estimate)}
                                        className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <Edit className="h-4 w-4 mr-2" />
                                        <span>Edit</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {estimate.status === 'accepted' && (
                                    <>
                                      <DropdownMenuItem 
                                        onClick={() => handleConvertToInvoice(estimate)}
                                        disabled={convertToInvoiceMutation.isPending}
                                        className="text-[var(--dash-accent)] hover:bg-[var(--dash-accent)]/10 cursor-pointer"
                                      >
                                        <BanknoteIcon className="h-4 w-4 mr-2" />
                                        <span>Convert to Invoice</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleStatusChange(estimate, 'rejected')}
                                        disabled={updateEstimateStatusMutation.isPending}
                                        className="text-[var(--dash-pill-inactive)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                      >
                                        <X className="h-4 w-4 mr-2" />
                                        <span>Client Said No</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {estimate.status === 'converted' && (
                                    <>
                                      <DropdownMenuItem 
                                        disabled
                                        className="text-green-400 dark:text-green-500 cursor-not-allowed opacity-60"
                                      >
                                        <Check className="h-4 w-4 mr-2" />
                                        <span>Converted to Invoice</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-600" />
                                  <DropdownMenuItem 
                                    onClick={() => viewEstimateDetails(estimate)}
                                    className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    <span>View Details</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => printEstimate(estimate)}
                                    className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                  >
                                    <Printer className="h-4 w-4 mr-2" />
                                    <span>Print</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-600" />
                                  <DropdownMenuItem 
                                    onClick={() => deleteEstimate(estimate)}
                                    className="text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 cursor-pointer"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    <span>Delete</span>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>

                          {/* Info Grid */}
                          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[var(--dash-border)]">
                            <div>
                              <div className="text-xs text-[var(--dash-text-muted)] mb-0.5">Client</div>
                              <div className="text-sm font-medium text-[var(--dash-text)] truncate">
                                {estimate.client?.firstName && estimate.client?.lastName 
                                  ? `${estimate.client.firstName} ${estimate.client.lastName}`
                                  : estimate.client?.first_name && estimate.client?.last_name 
                                  ? `${estimate.client.first_name} ${estimate.client.last_name}`
                                  : estimate.client?.name 
                                  ? estimate.client.name
                                  : estimate.client_name
                                  ? estimate.client_name
                                  : 'No Client'}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-[var(--dash-text-muted)] mb-0.5">Amount</div>
                              <div className="text-sm font-semibold text-[var(--dash-accent)]">
                                {formatCurrency(estimate.total || 0)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-[var(--dash-text-muted)] mb-0.5">Status</div>
                              <div>{getStatusBadge(estimate.status)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-[var(--dash-text-muted)] mb-0.5">Date</div>
                              <div className="text-sm text-[var(--dash-text)]">
                                {new Date(estimate.created_at || estimate.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                          </div>

                          {/* Expandable Line Items */}
                          {expandedEstimates.has(estimate.id) && (
                            <div className="pt-3 border-t border-[var(--dash-border)]">
                              <EstimateLineItems 
                                items={estimate.items || []}
                                estimateNumber={estimate.estimate_number || estimate.estimateNumber || 'N/A'}
                                className="mt-2"
                              />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
            </div>
        </main>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
        <AlertDialogContent className="bg-slate-800 border-slate-600">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-200">{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="remodra-button-outline">
              {confirmDialog.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDialog.action}
              className="remodra-button"
            >
              {confirmDialog.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
